import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button, Loading, Modal, Select } from '@/components/ui/daisy';
import { useModalDialog } from '@/components/ui/daisy/modal/hooks';
import { ORDENABLE_PAGE_SIZE } from '@/constants';
import useListService from '@/data/hook/useListService';
import {
  courseModulesQueryKeys,
  coursePathsQueryKeys,
} from '@/data/services/querykeys';
import CoursePath from '@/models/CoursePath';
import PedContentCard from '../ped-content/PedContentCard';

type EvaluationTemplateApplyModalProps = {
  courseLineId: number;
  appliedCoursePaths: CoursePath[];
  onApply: (coursePaths: CoursePath[]) => void;
  isOpen: boolean;
  onClose: () => void;
};

export default function EvaluationTemplateApplyModal({
  courseLineId,
  appliedCoursePaths,
  onApply,
  isOpen,
  onClose,
}: EvaluationTemplateApplyModalProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });

  const { ref, close } = useModalDialog(isOpen, onClose);

  const { results: modules, isInitialLoading: isLoadingModules } =
    useListService(courseModulesQueryKeys.list(courseLineId));

  const [selectedModuleId, setSelectedModuleId] = useState<number | null>(null);
  const [pendingCoursePaths, setPendingCoursePaths] =
    useState<CoursePath[]>(appliedCoursePaths);

  const { results: coursePaths, isInitialLoading: isLoadingCoursePaths } =
    useListService({
      ...coursePathsQueryKeys.list({
        courseLineId: [courseLineId],
        moduleId: selectedModuleId ? [selectedModuleId] : undefined,
        pageSize: ORDENABLE_PAGE_SIZE,
      }),
      enabled: !!selectedModuleId,
    });

  const findAppliedIndex = (coursePathId: number) =>
    pendingCoursePaths.findIndex(field => field.id === coursePathId);

  const onAdd = (coursePath: CoursePath) => {
    setPendingCoursePaths(prev => [...prev, coursePath]);
  };

  const onRemove = (appliedIndex: number) =>
    setPendingCoursePaths(prev =>
      prev.filter((_, index) => index !== appliedIndex),
    );

  useEffect(() => {
    if (!selectedModuleId && modules[0]?.id) setSelectedModuleId(modules[0].id);
  }, [modules, selectedModuleId]);

  useEffect(() => {
    if (isOpen) setPendingCoursePaths(appliedCoursePaths);
  }, [isOpen, appliedCoursePaths]);

  return (
    <Modal ref={ref}>
      <Modal.Box className="max-w-xl flex flex-col gap-6">
        <Modal.Close />
        <div className="flex flex-col gap-1 pr-10">
          <h2 className="text-lg font-semibold text-base-content">
            {t('applyTemplateModalTitle')}
          </h2>
          <span className="text-sm text-base-content/70">
            {t('applyTemplateModalDescription')}
          </span>
        </div>

        <Select.Fieldset label={t('selectModuleLabel')}>
          <Select
            value={selectedModuleId ?? ''}
            className="w-full"
            onChange={event =>
              setSelectedModuleId(Number(event.currentTarget.value))
            }
            disabled={isLoadingModules || !modules.length}
          >
            <Select.Option value="" disabled>
              {t('selectModulePlaceholder')}
            </Select.Option>
            {modules.map(module => (
              <Select.Option key={module.id} value={module.id}>
                {module.name}
              </Select.Option>
            ))}
          </Select>
        </Select.Fieldset>

        <ConditionalRenderer condition={isLoadingCoursePaths}>
          <div className="flex items-center justify-center py-12">
            <Loading variant="spinner" size="lg" color="primary" />
          </div>
        </ConditionalRenderer>

        <ConditionalRenderer
          condition={
            !isLoadingCoursePaths &&
            selectedModuleId != null &&
            !coursePaths.length
          }
        >
          <span className="text-sm text-base-content/70">
            {t('emptyCoursePathsForModule')}
          </span>
        </ConditionalRenderer>

        <ConditionalRenderer
          condition={!isLoadingCoursePaths && coursePaths.length > 0}
        >
          {coursePaths.map(coursePath => {
            const appliedIndex = findAppliedIndex(coursePath.id);
            const isApplied = appliedIndex >= 0;
            return (
              <PedContentCard
                key={coursePath.id}
                coursePath={coursePath}
                courseLineId={courseLineId}
                moduleId={selectedModuleId ?? 0}
                actions={
                  <PedContentModalAction
                    isApplied={isApplied}
                    onRemove={() => onRemove(appliedIndex)}
                    onAppend={() => onAdd(coursePath)}
                  />
                }
              />
            );
          })}
          <div className="flex justify-end gap-2">
            <Button type="button" className="grow bg-base-300" onClick={close}>
              {t('cancel')}
            </Button>
            <Button
              type="button"
              color="primary"
              onClick={() => {
                onApply(pendingCoursePaths);
                close();
              }}
              className="grow"
            >
              {t('save')}
            </Button>
          </div>
        </ConditionalRenderer>
      </Modal.Box>
      <Modal.Backdrop />
    </Modal>
  );
}

type PedContentModalActionProps = {
  isApplied: boolean;
  onRemove: () => void;
  onAppend: () => void;
};

const PedContentModalAction = ({
  isApplied,
  onRemove,
  onAppend,
}: PedContentModalActionProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });
  if (isApplied)
    return (
      <Button
        type="button"
        size="sm"
        variant="soft"
        color="error"
        onClick={onRemove}
      >
        <MinusIcon className="w-4 h-4" />
        {t('removeCoursePath')}
      </Button>
    );
  return (
    <Button
      type="button"
      size="sm"
      color="primary"
      variant="soft"
      onClick={onAppend}
    >
      <PlusIcon className="w-4 h-4" />
      {t('addCoursePath')}
    </Button>
  );
};
